import React, { useState, Fragment, useEffect } from 'react';
import get from 'lodash/get';
import Alert from '@nintex-ux/react-core/dist/Alert/Alert';
import Table from '@nintex-ux/react-core/dist/Table';
import Dialog from './Dialog';
import Button from '@nintex-ux/react-core/dist/Button';
import History from '../../components/History/History';
import useFetch from '../../utils/useFetch';
import formatTime from '../../utils/formatTime';

const Jobs = ({ data, isLoading, error, getUrl, getNextPage, hasNextPage }) => {
  const history = useFetch();
  const antiForgeryToken = window.parent.document.getElementsByName("__RequestVerificationToken")[0].value;
  const recoverSchedule = useFetch().useHttp(global.location.origin , { headers: {'RequestVerificationToken': antiForgeryToken }});
  const [histories, setHistories] = useState({});
  const [metadata, setMetadata] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [recoveryJob, setRecoverJob] = useState(null);

  useEffect(() => {
    if (!data.length) {
      setCurrentPage(1);
    }
  }, [data.length]);

  const handleRowCommand = async ({ cell, row }) => {
    if (cell.value === 'Metadata') {
      setMetadata(get(row, 'metadata.data'));
      return;
    }

    if (cell.value === 'Recover Job') {
      if (row.operation.length > 1 || row.status.length > 1) {
        return;
      }
 
      const [request] = recoverSchedule;
      const data = await request.post(getUrl('recover', { jobId: row.id }));

      if (recoverSchedule.response?.ok) {
        setRecoverJob(data);
      } else {
        setRecoverJob({ message: 'Error to recover job.' });
      }

      return;
    }

    // Show history. Assume cell.value === 'History'
    if (histories[row.id]) {
      setHistories(currentState => ({ ...currentState, [row.id]: null }));
      return;
    }

    setHistories(currentState => ({ ...currentState, [row.id]: { isLoading: true } }));

    try {
      const data = await history.get(getUrl('history', { jobId: row.id }));
      if (!data) {
        setHistories(currentState => ({
          ...currentState,
          [row.id]: { isLoading: false, error: 'Error fetching history.' },
        }));
      } else {
        setHistories(currentState => ({
          ...currentState,
          [row.id]: { isLoading: false, data: data.histories },
        }));
      }
    } catch (e) {
      setHistories(currentState => ({
        ...currentState,
        [row.id]: { isLoading: false, error: 'Error fetching history.' },
      }));
    }
  };

  const movePage = page => () => {
    setCurrentPage(page);
    if (!data[page - 1] && hasNextPage) {
      getNextPage();
    }
  };

  const getData = () => {
    if (!data.length || !data[currentPage - 1]) return [];
    return data[currentPage - 1].jobs;
  };

  const renderCell = (data, key) => (
    <div className="cell-row" key={key}>
      {data}
    </div>
  );

  const renderJobStatus = ({ data }) => (
    <td>{data.map((d, i) => renderCell(d === 'InProgress' ? 'In Progress' : d, 'status-' + i))}</td>
  );

  const renderJobCreatedTime = ({ data }) => <td>{data.map((d, i) => renderCell(formatTime(d), 'ct-' + i))}</td>;

  const renderOperation = ({ data }) => <td>{data.map((d, i) => renderCell(d, 'op-' + i))}</td>;

  const renderScheduleVersion = ({ data }) => (
    <td width="30" align="center">
      {renderCell(data, 'version')}
    </td>
  );

  const renderOccurrenceCount = ({ data }) => (
    <td width="30" align="center">
      {renderCell(data)}
    </td>
  );

  const renderHistory = row => (
    <tr key={`${row.id}-history`}>
      <td colSpan="7">
        <div>
          <h3>History</h3>
          <History
            getUrl={getUrl}
            data={histories[row.id].data}
            isLoading={histories[row.id].isLoading}
            error={histories[row.id].error}
          />
        </div>
      </td>
    </tr>
  );

  const renderTable = () => (
    <Table
      data={getData().map(processData)}
      customComponents={{
        createdTime: renderJobCreatedTime,
        status: renderJobStatus,
        operation: renderOperation,
        scheduleVersion: renderScheduleVersion,
        occurrenceCount: renderOccurrenceCount,
      }}
      columns={[
        {
          title: 'Created Time',
          field: 'createdTime',
        },
        {
          title: 'Operation',
          field: 'operation',
        },
        {
          title: 'Schedule Version',
          field: 'scheduleVersion',
        },
        {
          title: 'Occurrence Count',
          field: 'occurrenceCount',
        },
        {
          title: 'Status',
          field: 'status',
        },
        {
          title: '',
          field: 'metadata',
        },
        {
          title: '',
          field: 'history',
        },
        {
          title: '',
          field: 'recover',
        },
      ]}
      isRowEditing={row => {
        return {
          isEditing: histories[row.id],
          replace: false,
        };
      }}
      renderEditableRow={renderHistory}
      onRowCommand={handleRowCommand}
    />
  );

  const renderPagination = position => {
    if (!data.length) return null;

    const list = data.map(d => (
      <Button
        key={d.page}
        disabled={isLoading || d.page === currentPage}
        onClick={movePage(d.page)}
      >
        {`${d.page} ${d.page === currentPage ? '(current)' : ''}`}
      </Button>
    ));

    return (
      <div className={`pagination at-${position}`}>
        {list}{' '}
        <Button disabled={isLoading || !hasNextPage} onClick={movePage(data.length + 1)}>
          Next
        </Button>
      </div>
    );
  };

  const renderLoading = () => 'Loading...';
  const renderError = () => 'Error fetching jobs.';

  const processData = data => {
    var result = {
      id: data[0].id,
      createdTime: {
        component: 'createdTime',
        data: data.map(d => d.createdTime),
      },
      status: {
        component: 'status',
        data: data.map(d => d.status),
      },
      operation: {
        component: 'operation',
        data: data.map(d => d.operation),
      },
      scheduleVersion: {
        component: 'scheduleVersion',
        data: data[0].scheduleVersion,
      },
      occurrenceCount: {
        component: 'occurrenceCount',
        data: get(data[0], 'metadata.OccurrenceCount'),
      },
      metadata: {
        component: 'link',
        value: 'Metadata',
        data: data[0].metadata,
      },
      history: {
        component: 'link',
        value: 'History',
      },
    };

    if (data.length === 1) {
      result.recover = {
        component: 'link',
        value: 'Recover Job',
      };
    }

    return result;
  };

  const renderMessage = () => (
    <Alert type="warning">
      Last few groups of jobs might not be complete. Look at the first few jobs of the next page to
      be sure.
    </Alert>
  );

  const dataToUse = getData();

  return (
    <Fragment>
      {renderPagination('top')}
      {dataToUse.length ? renderTable() : null}
      {dataToUse.length && hasNextPage ? renderMessage() : null}
      {dataToUse.length && !isLoading ? renderPagination('bottom') : null}
      {isLoading ? renderLoading() : null}
      {error ? renderError() : null}

      <Dialog
        open={metadata != null}
        header="Job Metadata"
        yesLabel="Close"
        onAction={() => setMetadata(null)}
        onClose={() => setMetadata(null)}
      >
        <pre>{JSON.stringify(metadata, null, 2)}</pre>
      </Dialog>

      <Dialog
        open={recoveryJob != null}
        header="Recover Job"
        yesLabel="Close"
        onAction={() => setRecoverJob(null)}
        onClose={() => setRecoverJob(null)}
      >
        <pre>{JSON.stringify(recoveryJob, null, 2)}</pre>
      </Dialog>
    </Fragment>
  );
};


export default Jobs;
